/**
 * Aviso sin conexión + invitación a instalar la app (2026-10-07, reescrito en
 * JSX legible a partir del recuperado).
 *
 * Instalar, según dónde se abrió:
 *  - dentro de WhatsApp / Instagram / Facebook / TikTok: su navegador interno
 *    no instala ni recibe avisos → abrir en Chrome (Android) o Safari (iPhone);
 *  - iPhone / iPad: no hay botón de instalar: guía «Compartir → Agregar a inicio»;
 *  - Android y escritorio (Chrome / Edge): botón de un toque.
 * «Ahora no» lo oculta 14 días (antes era para siempre).
 */
import {
  rec_React as React,
  rec_Ri as IconoCompartir,
  rec_Rr as IconoDescargar,
  rec_es as sonido,
  rec_na as IconoSinRed,
  rec_ra as IconoCerrar,
  rec_uS as fechaHora,
} from "../../runtime/index.jsx";

// runtime/index.jsx importa este archivo antes de definir React (import circular).
const useEffect = (...a) => React.useEffect(...a);
const useState = (...a) => React.useState(...a);

const OCULTO = `gth_instalar_oculto`;
const DIAS_OCULTO = 14;

const ua = () => navigator.userAgent || ``;
export const esIOS = () => /iphone|ipad|ipod/i.test(ua()) || (navigator.platform === `MacIntel` && navigator.maxTouchPoints > 1);
export const esAndroid = () => /android/i.test(ua());
export const estaInstalada = () =>
  window.matchMedia?.(`(display-mode: standalone)`).matches || window.matchMedia?.(`(display-mode: minimal-ui)`).matches || window.navigator.standalone === true;
/** Navegadores internos de redes sociales: no instalan ni reciben avisos. */
export const navegadorInterno = () => {
  const u = ua();
  if (/WhatsApp/i.test(u)) return `WhatsApp`;
  if (/Instagram/i.test(u)) return `Instagram`;
  if (/FBAN|FBAV|FB_IAB|Messenger/i.test(u)) return `Facebook`;
  if (/TikTok|musical_ly|Bytedance/i.test(u)) return `TikTok`;
  if (/Line\//i.test(u)) return `LINE`;
  if (esAndroid() && /; wv\)/.test(u)) return `esta aplicación`;
  return null;
};

function oculto() {
  try {
    const v = localStorage.getItem(OCULTO);
    if (!v) return false;
    if (v === `1`) return false; // marca vieja («para siempre»): se vuelve a ofrecer
    return Date.now() - Number(v) < DIAS_OCULTO * 864e5;
  } catch {
    return false;
  }
}

function SinConexion({ desde }) {
  return (
    <div className="fixed inset-x-0 bottom-0 z-50 flex flex-wrap items-center justify-center gap-x-2 bg-(--warning-bg) px-4 py-2 text-center text-sm text-(--text-h)"
      style={{ paddingBottom: `max(.5rem, env(safe-area-inset-bottom))` }}>
      <span className="flex items-center gap-2">
        <IconoSinRed className="h-4 w-4 shrink-0" />
        Sin conexión — puedes seguir: lo que hagas se manda cuando vuelva la señal.
      </span>
      {desde && <span className="text-xs text-(--text)">Lo que ves es de {fechaHora(new Date(desde).toISOString())}.</span>}
    </div>
  );
}

function Tarjeta({ titulo, children, acciones, onCerrar }) {
  return (
    <div className="animar-entrada fixed inset-x-3 z-50 mx-auto flex max-w-md items-start gap-3 rounded-2xl border border-(--border) bg-(--bg) p-3 shadow-(--shadow)"
      style={{ bottom: `max(.75rem, calc(env(safe-area-inset-bottom) + .5rem))` }} role="dialog" aria-label={titulo}>
      <div className="min-w-0 flex-1 text-sm">
        <p className="font-medium text-(--text-h)">{titulo}</p>
        <div className="mt-0.5 text-xs text-(--text)">{children}</div>
        {acciones && <div className="mt-2 flex flex-wrap gap-2">{acciones}</div>}
      </div>
      <button type="button" onClick={onCerrar} aria-label="Ahora no" className="shrink-0 rounded-lg p-1 text-(--text) transition hover:bg-(--accent-bg)">
        <IconoCerrar className="h-4 w-4" />
      </button>
    </div>
  );
}

const claseBoton = `shrink-0 rounded-full bg-(--accent) px-3 py-1.5 text-xs font-medium text-(--accent-fg) transition hover:opacity-90`;
const claseBotonSec = `shrink-0 rounded-full border border-(--border) px-3 py-1.5 text-xs font-medium text-(--text-h) transition hover:bg-(--accent-bg)`;

export function InstalacionYConexion() {
  const [sinRed, setSinRed] = useState(!navigator.onLine);
  const [desactualizado, setDesactualizado] = useState(null);
  const [aviso, setAviso] = useState(null); // evento beforeinstallprompt
  const [mostrar, setMostrar] = useState(false);
  const [copiado, setCopiado] = useState(false);

  useEffect(() => {
    const enLinea = () => { setSinRed(false); setDesactualizado(null); };
    const fueraDeLinea = () => { setSinRed(true); sonido(`error`); };
    const datosViejos = (e) => setDesactualizado(e.detail?.en ?? null);
    window.addEventListener(`online`, enLinea);
    window.addEventListener(`offline`, fueraDeLinea);
    window.addEventListener(`datos-desactualizados`, datosViejos);
    return () => {
      window.removeEventListener(`online`, enLinea);
      window.removeEventListener(`offline`, fueraDeLinea);
      window.removeEventListener(`datos-desactualizados`, datosViejos);
    };
  }, []);

  useEffect(() => {
    if (estaInstalada() || oculto()) return;
    const alOfrecer = (e) => { e.preventDefault(); setAviso(e); setMostrar(true); };
    const alInstalar = () => { setAviso(null); setMostrar(false); };
    window.addEventListener(`beforeinstallprompt`, alOfrecer);
    window.addEventListener(`appinstalled`, alInstalar);
    // iPhone y navegadores internos no avisan: se ofrece la guía de una vez.
    if (esIOS() || navegadorInterno()) setMostrar(true);
    return () => {
      window.removeEventListener(`beforeinstallprompt`, alOfrecer);
      window.removeEventListener(`appinstalled`, alInstalar);
    };
  }, []);

  const cerrar = () => {
    try { localStorage.setItem(OCULTO, String(Date.now())); } catch {}
    setMostrar(false);
    setAviso(null);
  };

  if (sinRed) return <SinConexion desde={desactualizado} />;
  if (!mostrar) return null;

  const interno = navegadorInterno();
  if (interno) {
    const enlace = window.location.href;
    const abrirEnChrome = () => {
      const u = new URL(enlace);
      window.location.href = `intent://${u.host}${u.pathname}${u.search}#Intent;scheme=${u.protocol.replace(`:`, ``)};package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(enlace)};end`;
    };
    const copiar = async () => {
      try { await navigator.clipboard.writeText(enlace); setCopiado(true); } catch { setCopiado(false); }
    };
    return (
      <Tarjeta titulo={`Ábrelo en ${esIOS() ? `Safari` : `Chrome`}`} onCerrar={cerrar}
        acciones={<>
          {esAndroid() && <button type="button" className={claseBoton} onClick={abrirEnChrome}>Abrir en Chrome</button>}
          <button type="button" className={esAndroid() ? claseBotonSec : claseBoton} onClick={copiar}>{copiado ? `¡Enlace copiado!` : `Copiar enlace`}</button>
        </>}>
        Lo abriste dentro de {interno}: desde ahí no se puede instalar la app ni recibir avisos.
        {esIOS() && ` Toca ··· y elige «Abrir en Safari», o copia el enlace y pégalo en Safari.`}
      </Tarjeta>
    );
  }

  if (esIOS()) {
    return (
      <Tarjeta titulo="Instala la app en tu iPhone" onCerrar={cerrar}>
        <ol className="mt-1 flex flex-col gap-1">
          <li>1. Toca <IconoCompartir className="inline h-3.5 w-3.5 align-[-2px]" /> <strong>Compartir</strong> (abajo en Safari).</li>
          <li>2. Elige <strong>«Agregar a inicio»</strong>.</li>
          <li>3. Toca <strong>Agregar</strong>. Ábrela desde el ícono para recibir avisos.</li>
        </ol>
      </Tarjeta>
    );
  }

  if (!aviso) return null;
  const instalar = async () => {
    aviso.prompt();
    await aviso.userChoice.catch(() => {});
    setAviso(null);
    setMostrar(false);
  };
  return (
    <Tarjeta titulo="Instala la app" onCerrar={cerrar}
      acciones={<button type="button" onClick={instalar} className={claseBoton}><IconoDescargar className="mr-1 inline h-3.5 w-3.5" />Instalar</button>}>
      Ábrela desde un ícono, sin buscar la página, y recibe avisos aunque esté cerrada.
    </Tarjeta>
  );
}
